import { useState } from 'react';
import type { QuizQuestion } from '../data/quizQuestions';
import { useI18n } from '../i18n';

const CATEGORY_LABEL: Record<QuizQuestion['category'], string> = { geography: '地理', history: '历史', finance: '财务' };

interface Props {
  question: QuizQuestion;
  onResolve: (answerIndex: number) => void;
  onSnooze: () => void;
  onDismiss: () => void;
  onBuildHub?: () => void;
  onBuyAircraft?: () => void;
  hasHubVoucher?: boolean;
  hasAircraftVoucher?: boolean;
  penaltyCount?: number;
}

export function QuizModal({ question, onResolve, onSnooze, onDismiss, onBuildHub, onBuyAircraft, hasHubVoucher, hasAircraftVoucher }: Props) {
  const { t } = useI18n();
  const [chosen, setChosen] = useState<number | null>(null);
  const [resolved, setResolved] = useState(false);

  const isCorrect = chosen === question.answer;
  const resultMessage = isCorrect
    ? t('ui.答对了！获得城市调研洞察（营收增益 3% · 持续 4 周，声誉 +1）')
    : t('ui.答错了，本次调研未形成可用洞察，不会产生经营惩罚。');

  function answer(optionIndex: number) {
    setChosen(optionIndex);
  }

  function confirm() {
    if (!resolved) {
      setResolved(true);
      onResolve(chosen ?? -1);
    }
  }

  return (
    <div className="quiz-modal-backdrop" role="presentation">
      <section className="quiz-modal-sheet" role="dialog" aria-modal="true" aria-label={t('ui.城市调研')}>
        <header className="quiz-modal-header">
          <div>
            <span className="eyebrow">{t('ui.城市调研')}</span>
            <h2>{t('ui.完成调研可获得轻量经营洞察')}</h2>
          </div>
          <button className="icon-button" onClick={onSnooze} aria-label={t('ui.稍后处理')} title={t('ui.稍后处理')}>×</button>
        </header>

        <div className="quiz-modal-question">
          <div className="quiz-tags">
            <span className="tag accent">{question.city}</span>
            <span className="tag">{CATEGORY_LABEL[question.category]}</span>
          </div>
          <p>{question.question}</p>
        </div>

        <ol className="quiz-modal-options">
          {question.options.map((option, i) => {
            let cls = 'quiz-modal-option';
            if (chosen !== null) {
              if (i === question.answer) cls += ' correct';
              else if (i === chosen) cls += ' wrong';
              else cls += ' dim';
            }
            return (
              <li key={i}>
                <button className={cls} onClick={() => answer(i)} disabled={chosen !== null}>
                  <i>{String.fromCharCode(65 + i)}</i>
                  <span>{option}</span>
                  {chosen !== null && i === question.answer && <b>✓</b>}
                  {chosen !== null && i === chosen && i !== question.answer && <b className="red">✕</b>}
                </button>
              </li>
            );
          })}
        </ol>

        {chosen !== null && (
          <div className="quiz-modal-result">
            <p className={isCorrect ? 'green' : 'red'}>{resultMessage}</p>
            <div className="quiz-explanation">
              <strong>{t('ui.题目解析')}</strong>
              <p>{question.explanation}</p>
            </div>
            {isCorrect && !resolved && (hasHubVoucher || hasAircraftVoucher) && (
              <div className="quiz-modal-vouchers">
                {hasHubVoucher && onBuildHub && (
                  <button className="primary-button" onClick={onBuildHub}>{t('ui.使用 7 折枢纽券')}</button>
                )}
                {hasAircraftVoucher && onBuyAircraft && (
                  <button className="primary-button" onClick={onBuyAircraft}>{t('ui.使用 7 折飞机券')}</button>
                )}
              </div>
            )}
            {!resolved && (
              <button className="primary-button" onClick={confirm}>
                {isCorrect ? t('ui.领取洞察并结算') : t('ui.结束调研并结算')}
              </button>
            )}
          </div>
        )}
        {chosen === null && (
          <div className="quiz-modal-footer">
            <button className="secondary-action" onClick={onSnooze}>{t('ui.稍后处理')}</button>
            <button className="text-action" onClick={onDismiss}>{t('ui.忽略本次调研')}</button>
          </div>
        )}
      </section>
    </div>
  );
}
